import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { UseFormReturn } from 'react-hook-form';

import { JobPostingForm } from '@/components/pages/job-posting/types';
import { useJobPostingForm } from '@/components/pages/job-posting/use-job-posting-form';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';
import { queryKeys } from '@/query-keys';

import type { BaseSyntheticEvent } from 'react';

const SELECTED_CHANNEL_SLUGS_KEY = 'job-posting-selected-channel-slugs';
const SELECTED_OPENING_IDS_KEY = 'job-posting-selected-opening-ids';
const UNSUPPORTED_OPENINGS_KEY = 'job-posting-unsupported-openings';

interface JobPostingContextType {
  openChannelsDialog: boolean;
  setOpenChannelsDialog: Dispatch<SetStateAction<boolean>>;
  selectedChannelSlugs: string[];
  setSelectedChannelSlugs: Dispatch<SetStateAction<string[]>>;
  selectedOpeningIds: string[];
  setSelectedOpeningIds: Dispatch<SetStateAction<string[]>>;
  unsupportedOpenings: Record<string, string[]>;
  setUnsupportedOpenings: Dispatch<SetStateAction<Record<string, string[]>>>;
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  hookForm: UseFormReturn<JobPostingForm>;
  clearJobPostingStorage: () => void;
  totalBudget: number;
  handlePostJob: (e?: BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;
  setRecommendedChannelSlugs: Dispatch<SetStateAction<string[]>>;
  recommendedChannelSlugs: string[];
  poolOpeningsWithConflicts: string[] | undefined;
  setPoolOpeningsWithConflicts: Dispatch<SetStateAction<string[] | undefined>>;
}

export const JobPostingContext = createContext<
  JobPostingContextType | undefined
>(undefined);

export function JobPostingProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [selectedChannelSlugs, setSelectedChannelSlugs] = useLocalStorageState<
    string[]
  >(SELECTED_CHANNEL_SLUGS_KEY, []);
  const [selectedOpeningIds, setSelectedOpeningIds] = useLocalStorageState<
    string[]
  >(SELECTED_OPENING_IDS_KEY, []);
  const [unsupportedOpenings, setUnsupportedOpenings] = useLocalStorageState<
    Record<string, string[]>
  >(UNSUPPORTED_OPENINGS_KEY, {});
  const [recommendedChannelSlugs, setRecommendedChannelSlugs] = useState<
    string[]
  >([]);
  const [openChannelsDialog, setOpenChannelsDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [poolOpeningsWithConflicts, setPoolOpeningsWithConflicts] =
    useState<string[]>();
  const hookFormRef = useRef<UseFormReturn<JobPostingForm> | null>(null);

  const clearJobPostingStorage = () => {
    localStorage.removeItem(SELECTED_CHANNEL_SLUGS_KEY);
    localStorage.removeItem(SELECTED_OPENING_IDS_KEY);
    localStorage.removeItem(UNSUPPORTED_OPENINGS_KEY);
    setSelectedChannelSlugs([]);
    setSelectedOpeningIds([]);
    setUnsupportedOpenings({});
    setRecommendedChannelSlugs([]);
    setCurrentStep(1);
    setOpenChannelsDialog(false);
    setPoolOpeningsWithConflicts(undefined);
    queryClient.removeQueries({ queryKey: queryKeys.jobPosting.poolConflicts });
    hookFormRef.current?.reset({});
  };

  const { hookForm, handlePostJob, isSubmitting } = useJobPostingForm({
    clearJobPostingStorage,
  });
  hookFormRef.current = hookForm;

  const channels = hookForm.watch('channels');
  const totalBudget = !channels
    ? 0
    : Object.values(channels).reduce(
        (acc, channel) => acc + (channel.totalChannelBudget ?? 0),
        0,
      );

  return (
    <JobPostingContext.Provider
      value={{
        openChannelsDialog,
        setOpenChannelsDialog,
        selectedChannelSlugs,
        setSelectedChannelSlugs,
        setRecommendedChannelSlugs,
        recommendedChannelSlugs,
        selectedOpeningIds,
        setSelectedOpeningIds,
        currentStep,
        setCurrentStep,
        hookForm,
        totalBudget,
        clearJobPostingStorage,
        handlePostJob,
        isSubmitting,
        unsupportedOpenings,
        setUnsupportedOpenings,
        poolOpeningsWithConflicts,
        setPoolOpeningsWithConflicts,
      }}
    >
      {children}
    </JobPostingContext.Provider>
  );
}

export function useJobPostingContext() {
  const context = useContext(JobPostingContext);

  if (!context) {
    throw new Error(
      'useJobPostingContext must be used within a JobPostingProvider',
    );
  }

  return context;
}
